import { Component } from "react";
import { connect } from "react-redux";
import {
  getCategoryTreeFromDB,
  saveNewCategoryFilterSelection,
} from "../../redux/actions/categoryAction";
import { getItemsToDisplay } from "../../redux/actions/itemActions";
import { fetchRecipesFromShopify } from "../../redux/actions/recipeActions";
import { saveNewSearch } from "../../redux/actions/searchActions";

import PaginationClicker from "../naviagtions/PaginationClicker";
import ItemCard from "./ItemCard/ItemCard";
import ItemCardLoading from "./ItemCard/ItemCardLoading";
import RecipeCard from "../recipes/RecipeCard";
import { Recipe } from "../../types/shopify";
import { RecipesState } from "../../types/recipes";

interface State {
  isLoading: boolean;
  itemsToDisplay: ItemData[];
}

interface Props {
  search: SearchState;
  categories: CategoryState;
  items: ItemsState;
  recipes: RecipesState;
  getItemsToDisplay: any;
  fetchRecipesFromShopify: (blogHandle?: string, cursor?: string) => Promise<Recipe[]>;
  saveNewSearch: (searchTerm: string, enhancedRanking?: boolean) => void;
  saveNewCategoryFilterSelection: (selectedNode: CategoryTreeNode | null) => void;
}

class ItemsGrid extends Component<Props, State> {
  state = {
    isLoading: true,
    itemsToDisplay: [],
  };
  
  // Check if the current category is Recipes
  isRecipesCategory = () => {
    const selectedNode = this.props.categories.selectedNode;
    return selectedNode && selectedNode.name.toLowerCase() === 'recipes';
  };

  // Force a fresh fetch of recipes to solve mobile navigation issues
  fetchRecipes = () => {
    // Clear recipes loading state
    this.setState({ isLoading: true });
    // Force fetch from API by using a unique cursor value
    const timestamp = Date.now();
    this.props.fetchRecipesFromShopify('recipes', `refresh_${timestamp}`);
  };

  componentDidMount = () => {
    // Check if we're in the Recipes category
    if (this.isRecipesCategory()) {
      // Force a fresh fetch of recipes when in Recipes category
      this.fetchRecipes();
    } else {
      // Otherwise find products as usual
      this.getItems();
    }
  };

  componentDidUpdate = (prevProps: Props) => {
    // if search changes, clear the category filter
    if (prevProps.search.searchTerm !== this.props.search.searchTerm) {
      // We need to properly dispatch the action to clear category selection
      this.props.saveNewCategoryFilterSelection(null);
    }

    // if category changes, clear the search term
    if (prevProps.categories.selectedNode !== this.props.categories.selectedNode) {
      // Properly dispatch the action to clear the search term
      if (this.props.search.searchTerm) {
        console.log('Category changed - clearing search term');
        this.props.saveNewSearch("");
      }
      
      // If we're now in the Recipes category, force a fresh fetch of recipes
      if (this.isRecipesCategory()) {
        // Use our fetchRecipes method that bypasses caching
        this.fetchRecipes();
        return; // Skip normal item fetching
      }
    }

    // Check if the category was changed by CategoryDrillDownFilter or if search term changed
    const categoryChanged = prevProps.categories.selectedNode !== this.props.categories.selectedNode;
    const searchChanged = prevProps.search.searchTerm !== this.props.search.searchTerm;
    
    // We only want to fetch products here if this wasn't triggered by a CategoryDrillDownFilter click
    // Check if category was changed but the items haven't been fetched yet
    if (searchChanged || (categoryChanged && !this.props.items.isLoading)) {
      console.log('ItemsGrid: Category/search changed - initiating product fetch');
      
      // get new items to display
      this.getItems();

      // show loading screen 
      this.setState({ isLoading: true });
    } else if (categoryChanged) {
      // If category changed but items are already being loaded, just show loading screen
      // This happens when CategoryDrillDownFilter initiated the product fetch
      console.log('ItemsGrid: Category changed but products already being fetched, showing loading only');
      this.setState({ isLoading: true });
    }

    // If we're in the Recipes category and recipes have been updated, update state
    if (this.isRecipesCategory() && 
        prevProps.recipes.shopifyRecipes !== this.props.recipes.shopifyRecipes) {
      this.setState({ isLoading: false });
    }
    
    // if redux items state updates, display them
    else if (prevProps.items.itemsToDisplay !== this.props.items.itemsToDisplay) {
      this.setState({
        itemsToDisplay: this.props.items.itemsToDisplay,
        isLoading: false,
      });
    }
  };

  getItems = () => {
    const searchTerm = this.props.search.searchTerm;
    const categoryFilterId = this.props.categories.selectedNode?.crawler_id;
    const itemFilters = this.props.items.appliedFilters;
    // If we're in a category view, ALWAYS use Collection Order (MANUAL) sort
    // This ensures we maintain Shopify's manual sort order
    const sortType = categoryFilterId ? 'Collection Order' : this.props.items.sortTypeName;
    const isHalfThePriceClicked = this.props.items.isHalfThePriceClicked;
    
    console.log(`ItemsGrid requesting items with sort: ${sortType}, categoryId: ${categoryFilterId}`);

    this.props.getItemsToDisplay(
      searchTerm,
      categoryFilterId,
      itemFilters,
      sortType,
      isHalfThePriceClicked
    );
  };

  getWhiteSpaceHeight = () => {
    const headerHeight =
      document.getElementById("header")?.getBoundingClientRect().bottom || 0;
    const whiteSpaceHeight = window.innerHeight - headerHeight;
    const newItemsHeight = window.innerHeight - whiteSpaceHeight;
    const maxMarginTop = 200;

    const dynamicHeight =
      newItemsHeight > maxMarginTop
        ? maxMarginTop + "px"
        : newItemsHeight + "px";

    return dynamicHeight;
  };

  render() {
    const itemsToDisplay = this.state.itemsToDisplay;
    const pageSpacerMarginTop = this.getWhiteSpaceHeight();
    const isRecipes = this.isRecipesCategory();
    const shopifyRecipes = this.props.recipes.shopifyRecipes || [];

    return (
      <div className="u-w-all u-h-all u-background--grey">
        {/* Show loading state */}
        {this.state.isLoading && (
          <div className="u-background--grey u-w-all u-h-all u-grid--items u-ph--16 u-pb--16 u-mb--16">
            <ItemCardLoading />
            <ItemCardLoading />
            <ItemCardLoading />
            <ItemCardLoading />
            <ItemCardLoading />
          </div>
        )}

        {/* Display Recipes when in Recipes category */}
        {isRecipes && !this.state.isLoading && (
          <div className="u-background--grey u-pt--4--mobile u-ph--16 u-pb--16 u-mb--16">
            {shopifyRecipes.length > 0 ? (
              <div className="u-background--grey u-grid--items">
                {shopifyRecipes.map(recipe => (
                  <RecipeCard 
                    key={recipe.id} 
                    shopifyRecipe={recipe} 
                    isShopifyRecipe={true} 
                  />
                ))}
              </div>
            ) : (
              <div 
                className="u-main-font--large u-color-magenta u-semi-bold u-w-all u-p--32 u-ph--48 u-pb--16 u-mb--16"
                style={{ height: window.innerHeight }}
              >
                <p className="u-main-font--large">No recipes found</p>
                <p className="u-main-font--medium">Check back later for new recipes</p>
              </div>
            )}
          </div>
        )}

        {/* Show "no items found" message when not in Recipes category and no items */}
        {!isRecipes && itemsToDisplay.length === 0 && !this.state.isLoading && (
          <div
            className="u-main-font--large u-color-magenta u-semi-bold u-w-all u-p--32 u-ph--48 u-pb--16 u-mb--16"
            style={{ height: window.innerHeight }}
          >
            Sorry, we can't find any items for{" "}
            <span className="u-bold">"{this.props.search.searchTerm}"</span>
            <br />
            <span className="u-main-font--small">
              Please try again with fewer or different words
            </span>
          </div>
        )}

        {/* Display regular items when not in Recipes category */}
        {!isRecipes && itemsToDisplay.length > 0 && !this.state.isLoading && (
          <>
            <div className="u-background--grey u-grid--items u-pt--4--mobile u-ph--16 u-pb--16 u-mb--16">
              {itemsToDisplay.map((item, index) => (
                <ItemCard itemData={item} key={item["id"] + "-" + index} />
              ))}
            </div>
            <PaginationClicker spacerMarginTop={pageSpacerMarginTop} />
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  search: state.search,
  categories: state.categories,
  items: state.items,
  recipes: state.recipes,
});

export default connect(mapStateToProps, {
  saveNewCategoryFilterSelection,
  getCategoryTreeFromDB,
  getItemsToDisplay,
  fetchRecipesFromShopify,
  saveNewSearch,
})(ItemsGrid);
